// The heatmap's drawn tiles are kept in the browser across page loads (see
// `heatmapTileStore`). They're drawn from one rider's GPS tracks, so they
// belong to that rider and go once a page is someone else's. Only the script
// on every page imports this (and the worker, built on its own): imported by
// the React app too, it would become a chunk that every page loads.

export const HEATMAP_STORE_DB = 'velo-heatmap'

const OWNER_KEY = 'velo-heatmap-owner'

interface OwnerChange {
  owner: string | null
  deleteStored: boolean
}

/**
 * Whose the stored tiles are after a page of `userId`, and whether the ones
 * stored so far go first. A page with the heatmap claims them; any other
 * page leaves its own rider's alone and deletes anyone else's.
 */
export function ownerAfterPage(
  owner: string | null,
  userId: number,
  hasHeatmap: boolean,
): OwnerChange {
  const isOthers = owner !== null && owner !== String(userId)

  if (hasHeatmap) {
    return { owner: String(userId), deleteStored: isOthers }
  }

  return { owner: isOthers ? null : owner, deleteStored: isOthers }
}

/**
 * Run on every page, before the heatmap's workers start. Logging out
 * elsewhere, a session that ran out, another account or the end of an
 * impersonation all delete the tiles by the next page. Workers of a page
 * still open close the database when asked, so the delete doesn't wait for
 * that page to go.
 */
export function syncHeatmapStoreOwner(userId: number, hasHeatmap: boolean) {
  try {
    const owner = localStorage.getItem(OWNER_KEY)
    const change = ownerAfterPage(owner, userId, hasHeatmap)

    if (change.deleteStored) {
      indexedDB.deleteDatabase(HEATMAP_STORE_DB)
    }

    if (change.owner === null) {
      localStorage.removeItem(OWNER_KEY)
    } else if (change.owner !== owner) {
      localStorage.setItem(OWNER_KEY, change.owner)
    }
  } catch {
    // Storage off: the workers can't keep tiles either
  }
}

/** On logout, which clears the owner with the rest of localStorage. */
export function deleteHeatmapStore() {
  try {
    localStorage.removeItem(OWNER_KEY)
    indexedDB.deleteDatabase(HEATMAP_STORE_DB)
  } catch {
    // Storage off: nothing was kept
  }
}
